import { Component, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CoreDataUpdateService } from 'src/app/data-transfer/services/core-data-update-api-service';
import { CustomFieldDto } from 'src/app/data-transfer/entities/custom-field-dto';
import { CustomFieldAssignmentsProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';

@Component({
  selector: 'app-profile-field-tree',
  templateUrl: './profile-field-tree.component.html',
  styleUrls: ['./profile-field-tree.component.scss'],
})
export class ProfileFieldTreeComponent {
  private static readonly EXCLUDED_KEYS = new Set(['customFields', 'childPackagingUnitProfileId', 'multiMaterialProfileId', 'packagingComponentProfileId']);

  @Input() node: Record<string, unknown> | null = null;
  @Input() depth = 0;
  @Input() hideEvidenceFilter = false;
  @Input() customFieldOptions: CustomFieldDto[] = [];
  @Input() minimumRequirementKeys: string[] = [];

  constructor(
    private translocoService: TranslocoService,
    private coreDataService: CoreDataUpdateService
  ) {}

  get evidenceTypes() {
    return this.coreDataService.allDOCEvidenceTypes;
  }

  get booleanKeys(): string[] {
    return this.entries.filter(([, value]) => value === null || typeof value === 'boolean').map(([key]) => key);
  }

  get groupKeys(): string[] {
    return this.entries.filter(([, value]) => this.isPlainObject(value)).map(([key]) => key);
  }

  get hasEvidenceFilter(): boolean {
    return Array.isArray(this.node?.['excludedEvidenceTypes']);
  }

  get excludedEvidenceTypeIds(): string[] {
    return (this.node?.['excludedEvidenceTypes'] as string[]) ?? [];
  }

  get hasCustomFields(): boolean {
    return this.customFieldOptions.length > 0 && this.node != null;
  }

  private get customFieldIds(): Set<number> {
    const assignments = this.node?.['customFields'] as CustomFieldAssignmentsProfileDto | null;
    return new Set((assignments?.fields ?? []).filter(field => field.includeField).map(field => field.customFieldId));
  }

  isCustomFieldSelected(id: number): boolean {
    return this.customFieldIds.has(id);
  }

  toggleCustomField(id: number): void {
    if (!this.node) return;
    const ids = this.customFieldIds;
    if (ids.has(id)) {
      ids.delete(id);
    } else {
      ids.add(id);
    }
    this.node['customFields'] = { fields: [...ids].map(customFieldId => ({ customFieldId, includeField: true })) };
  }

  private get entries(): [string, unknown][] {
    if (!this.node) return [];
    return Object.entries(this.node).filter(([key]) => !ProfileFieldTreeComponent.EXCLUDED_KEYS.has(key));
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  isChecked(key: string): boolean {
    return !!this.node?.[key];
  }

  isMinimumRequirement(key: string): boolean {
    return this.minimumRequirementKeys.includes(key);
  }

  toggle(key: string): void {
    if (!this.node) return;
    this.node[key] = !this.isChecked(key);
  }

  onEvidenceTypesChange(ids: string[]): void {
    if (!this.node) return;
    this.node['excludedEvidenceTypes'] = ids;
  }

  groupNode(key: string): Record<string, unknown> {
    return this.node?.[key] as Record<string, unknown>;
  }

  fieldLabel(key: string): string {
    const i18nKey = `fieldNamesForExport.${key}`;
    const translated = this.translocoService.translate(i18nKey);
    return translated === i18nKey ? this.humanize(key) : translated;
  }

  private humanize(key: string): string {
    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/^./, character => character.toUpperCase())
      .trim();
  }
}
